import {
  aggregateWeekly,
  type BodyMeasurementEntryData,
  formatMeasurementValue,
  type TrendWeek,
} from "@/lib/body-measurements";
import {
  addDays,
  DAY_KEYS,
  type DayKey,
  isoWeekMonday,
  previousIsoWeek,
} from "@/lib/days";
import type {
  BiologicalSex,
  EnergyExpenditureEstimate,
  MealCondition,
  MealLogConfig,
  MealLogDayData,
  MealMetric,
} from "@/lib/schema";
import { orderedConditions } from "@/widgets/meal-log/conditions";
import {
  asMealLogDayData,
  dayTotals,
  isConditionMet,
} from "@/widgets/meal-log/scoring";

/**
 * kcal in a kilogram of body fat — the metric statement of the 3500 kcal/lb
 * rule. A constant of the model rather than a personal preference, so it is
 * deliberately not a config field (docs/meal-log-summary-design.md).
 */
export const KCAL_PER_KILOGRAM = 7700;

export interface ActivityLevel {
  factor: number;
  label: string;
}

/**
 * The factor is what gets stored; the label is what the user picks. A bare
 * "1,55" field would mean nothing without it.
 */
export const ACTIVITY_LEVELS: readonly ActivityLevel[] = [
  { factor: 1.2, label: "Stillesittende" },
  { factor: 1.375, label: "Lett aktiv (1–3 dager i uken)" },
  { factor: 1.55, label: "Moderat aktiv (3–5 dager i uken)" },
  { factor: 1.725, label: "Svært aktiv (6–7 dager i uken)" },
  { factor: 1.9, label: "Hardt fysisk arbeid" },
] as const;

export const DEFAULT_ACTIVITY_FACTOR = 1.55;

/**
 * The days every number on the card reads: finished **and** holding at least
 * one meal (CONTEXT.md: "Talt dag").
 *
 * Deliberately narrower than the day set meal-log scores, which counts a
 * finished empty day. This widget divides by its day set where scoring only
 * sums, so one stray finish on an unlogged day would contribute a whole day's
 * Energiforbruk to the deficit and pull the kcal average toward zero
 * (docs/adr/0011). Both halves of the rule carry weight: the finish says the
 * day is representative, the meal says there is something to represent.
 */
export function countedDays(days: Record<DayKey, unknown>): DayKey[] {
  return DAY_KEYS.filter((key) => {
    const data: MealLogDayData = asMealLogDayData(days[key]);
    return data.finished && data.meals.length > 0;
  });
}

/** Per-metric day totals for each counted day, in week order. */
export function countedDayTotals(
  days: Record<DayKey, unknown>,
  counted: DayKey[],
): Array<Record<MealMetric, number>> {
  return counted.map((key) => dayTotals(asMealLogDayData(days[key]).meals));
}

export interface MetricSpread {
  average: number;
  minimum: number;
  maximum: number;
}

/** Null when nothing was counted — every display site renders that as "—". */
export function metricSpread(
  totals: Array<Record<MealMetric, number>>,
  metric: MealMetric,
): MetricSpread | null {
  if (totals.length === 0) return null;
  const values = totals.map((t) => t[metric]);
  return {
    average: values.reduce((sum, v) => sum + v, 0) / values.length,
    minimum: Math.min(...values),
    maximum: Math.max(...values),
  };
}

export interface ConditionRow {
  /** Condition id, or the literal "kcal" for the condition-less kcal row. */
  key: string;
  metric: MealMetric;
  /** Null on the kcal row that exists only because no kcal condition does. */
  condition: MealCondition | null;
  spread: MetricSpread | null;
  /** How many counted days met the condition; null when there is none. */
  met: number | null;
  /** The counted-day count `met` is out of — no figure without its denominator. */
  total: number;
}

/**
 * One row per Condition, plus a condition-less kcal row when the instance has
 * no kcal condition — kcal is what the energy block is built on, and a
 * pure-tracking instance would otherwise render an empty table.
 *
 * Conditions render in Betingelsesrekkefølge (docs/adr/0021), the same order
 * they take in meal-log's own views. The condition-less kcal row is pinned
 * above them rather than sorted among them: it is not a Condition and has no
 * points to be ranked on.
 */
export function conditionRows(
  config: MealLogConfig,
  totals: Array<Record<MealMetric, number>>,
): ConditionRow[] {
  const ordered = orderedConditions(config.conditions);
  const rows: ConditionRow[] = ordered.map((condition) => ({
    key: condition.id,
    metric: condition.metric,
    condition,
    spread: metricSpread(totals, condition.metric),
    met: totals.filter((t) => isConditionMet(condition, t)).length,
    total: totals.length,
  }));
  if (ordered.some((c) => c.metric === "kcal")) return rows;
  return [
    {
      key: "kcal",
      metric: "kcal",
      condition: null,
      spread: metricSpread(totals, "kcal"),
      met: null,
      total: totals.length,
    },
    ...rows,
  ];
}

export interface EnergySummary {
  averageKcal: number;
  /** kcal eaten minus kcal burned, per counted day. Negative is a deficit. */
  energyBalance: number;
  /** kg the counted days imply. Signed: negative means lighter. */
  expectedChangeSoFar: number;
  /** The same average balance extrapolated across all seven days. */
  expectedChangeWeek: number;
}

/**
 * Null when there is nothing to say — no counted days, or no Energiforbruk
 * configured. Every quantity is a *change*, signed the same way, so a negative
 * balance produces a negative weight change and no label ever flips with the
 * data (CONTEXT.md: "Energibalanse").
 */
export function energySummary(
  totals: Array<Record<MealMetric, number>>,
  dailyEnergyExpenditure: number | undefined,
): EnergySummary | null {
  if (totals.length === 0) return null;
  if (
    typeof dailyEnergyExpenditure !== "number" ||
    !Number.isFinite(dailyEnergyExpenditure) ||
    dailyEnergyExpenditure <= 0
  ) {
    return null;
  }
  const averageKcal =
    totals.reduce((sum, t) => sum + t.kcal, 0) / totals.length;
  const energyBalance = averageKcal - dailyEnergyExpenditure;
  return {
    averageKcal,
    energyBalance,
    expectedChangeSoFar: (energyBalance * totals.length) / KCAL_PER_KILOGRAM,
    expectedChangeWeek: (energyBalance * 7) / KCAL_PER_KILOGRAM,
  };
}

/**
 * The last ISO week that has finished, relative to the viewed one — the only
 * week whose measured change can be compared with an expectation, because a
 * partial week's weigh-ins and its logged days cover different spans
 * (docs/adr/0013).
 */
export function reconciledWeek(
  year: number,
  weekNumber: number,
): { year: number; weekNumber: number } {
  return previousIsoWeek(year, weekNumber);
}

/** The given ISO week and the one before it, oldest first. */
function weightComparisonWeeks(year: number, weekNumber: number): TrendWeek[] {
  const previous = previousIsoWeek(year, weekNumber);
  return [
    { ...previous, monday: isoWeekMonday(previous.year, previous.weekNumber) },
    { year, weekNumber, monday: isoWeekMonday(year, weekNumber) },
  ];
}

/** Inclusive date range covering both weeks. */
export function weightComparisonRange(
  year: number,
  weekNumber: number,
): { startDate: string; endDate: string } {
  const weeks = weightComparisonWeeks(year, weekNumber);
  return {
    startDate: weeks[0].monday,
    endDate: addDays(weeks[1].monday, 6),
  };
}

/**
 * This ISO week's average weight minus the previous week's, in kg, signed.
 * Null when either week has no weight measurement.
 *
 * Average against average by necessity: a single weigh-in carries around a
 * kilo of water noise, enough to invert the sign of a real week's change.
 */
export function measuredWeightChange(
  entries: BodyMeasurementEntryData[],
  year: number,
  weekNumber: number,
): number | null {
  const [previous, current] = aggregateWeekly(
    entries,
    "weight",
    weightComparisonWeeks(year, weekNumber),
  );
  if (!previous || !current) return null;
  return current.average - previous.average;
}

/**
 * Mifflin-St Jeor resting metabolic rate in kcal/day — the current clinical
 * standard, more accurate than Harris-Benedict for overweight populations.
 */
export function restingMetabolicRate(input: {
  weightKilograms: number;
  heightCentimeters: number;
  age: number;
  sex: BiologicalSex;
}): number {
  return (
    10 * input.weightKilograms +
    6.25 * input.heightCentimeters -
    5 * input.age +
    (input.sex === "male" ? 5 : -161)
  );
}

export function ageFromBirthYear(birthYear: number, today: Date): number {
  return today.getFullYear() - birthYear;
}

/**
 * The figure the calculator's "Bruk" button stamps into
 * `dailyEnergyExpenditure`. Null while any input is still missing or invalid —
 * which is what keeps the button disabled.
 */
export function estimatedExpenditure(
  estimate: Partial<EnergyExpenditureEstimate>,
  today: Date,
): { restingRate: number; expenditure: number } | null {
  const { weightKilograms, heightCentimeters, birthYear, sex, activityFactor } =
    estimate;
  if (!isPositive(weightKilograms)) return null;
  if (!isPositive(heightCentimeters)) return null;
  if (!isPositive(activityFactor)) return null;
  if (sex !== "male" && sex !== "female") return null;
  if (typeof birthYear !== "number" || !Number.isInteger(birthYear))
    return null;
  const age = ageFromBirthYear(birthYear, today);
  if (age < 0 || age > 130) return null;
  const restingRate = restingMetabolicRate({
    weightKilograms,
    heightCentimeters,
    age,
    sex,
  });
  if (restingRate <= 0) return null;
  return {
    restingRate: Math.round(restingRate),
    expenditure: Math.round(restingRate * activityFactor),
  };
}

function isPositive(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/** Whole kcal, or "—". */
export function formatKcal(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return String(Math.round(value));
}

/**
 * A signed number for the quantities that are changes — Energibalanse and
 * every kilogram figure. `formatMeasurementValue` already carries the minus;
 * this adds the explicit plus, so a week over budget reads "+230" rather than
 * arriving unmarked among the negatives.
 */
export function formatSigned(text: string): string {
  if (text === "—") return text;
  if (text.startsWith("-") || text.startsWith("−") || text === "0") return text;
  return `+${text}`;
}

export function formatSignedKcal(value: number | null | undefined): string {
  return formatSigned(formatKcal(value));
}

export function formatSignedKilograms(
  value: number | null | undefined,
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return formatSigned(formatMeasurementValue(value, 2));
}
